import cx from 'classnames';
import {filter, isNumber, map} from 'lodash';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Icon, Timer, ScreenRecorderStatus, NetworkQuality, notifier} from 'components';
import {UserSessionContext, UserContext} from 'context';
import {useIsChangedTo, useObservable, useUserSession} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {pictureInPictureStore} from 'services';

import {SessionQueue} from '../SessionQueue';
import {UserCameraSwitcher} from '../UserCameraSwitcher';
import {SidebarView} from '../types';

const SESSION_T_KEYS = TRANSLATION_KEYS.COMMON.SESSION;
const SESSION_END_SOON_NOTIFICATION_AFTER_TIME = 60 * 1000; // 1 minute

export const InSessionTopPanel: React.FC = () => {
    const {t} = useTranslation();
    const userContext = useContext(UserContext);
    const {
        session, settings, sidebarView, fullScreenVideo, participantsNetworkQuality, videoDevices,
        videoPinParticipant,
        onSidebarViewChange, onToggleFullScreenVideo, onTogglePictureInPicture
    } = useContext(UserSessionContext);
    const {
        audioVideoActivity, localParticipant, canSwitchCamera, isPictureInPictureEnabled, audioVideoViewConference
    } = useUserSession();
    const [mobileActionsOpen, setMobileActionsOpen] = useState(false);
    const toggleMobileActionsOpen = useCallback(() => {
        setMobileActionsOpen((prev) => !prev);
    }, []);

    const pictureInPicture = useObservable(pictureInPictureStore);

    const [timeLeft, setTimeLeft] = useState<number>(null);
    const sessionEndSoon = useMemo(() =>
        session.maxDuration && isNumber(timeLeft) && timeLeft < SESSION_END_SOON_NOTIFICATION_AFTER_TIME,
    [session.maxDuration, timeLeft]);
    const showNotification = useIsChangedTo(sessionEndSoon, true);

    const sessionLimitEndDate: Date = useMemo(() => {
        const startDate = new Date(session.inSessionStartDate);
        return new Date(startDate.getTime() + session.maxDuration * 60 * 1000);
    }, [session]);

    const timer = useMemo(() => {
        if (sessionEndSoon) {
            return (<span>{t(SESSION_T_KEYS.LIMITS.MAX_DURATION_EXCEEDED_SOON_TIMER)}</span>);
        } else if (session.maxDuration) {
            return (
                <Timer
                    startDateNow
                    endDate={sessionLimitEndDate}
                    config={{optionalHours: true, minutes: true, seconds: true}}
                    onDiffChange={setTimeLeft}
                />
            );
        } else {
            return (
                <Timer
                    config={{optionalHours: true, minutes: true, seconds: true}}
                    startDate={session.inSessionStartDate}
                />
            );
        }
    }, [session, sessionEndSoon, sessionLimitEndDate, t]);

    const items = useMemo(() => [
        {
            content: (
                <div className='tw-flex tw-items-center'>
                    <Icon name='id' className='tw-mr-1' />
                    <span>{session.id}</span>
                </div>),
            hidden: sidebarView === SidebarView.Participants,
            mobile: true
        },
        {
            content: <SessionQueue />,
            hidden: userContext.auth?.sessionAccessOnly || !settings.session.queue?.enabled,
        },
        {
            content: <ScreenRecorderStatus />,
            hidden: !userContext.settings.userSession?.requireScreenRecorder
        },
        {
            content: timer
        },
        {
            content: <NetworkQuality value={participantsNetworkQuality?.[localParticipant.id]} />,
            hidden: !audioVideoActivity || !isNumber(participantsNetworkQuality?.[localParticipant.id])

        },
        {
            content: <Icon
                name={pictureInPicture.active ? 'multiscreen-off' : 'multiscreen-on'}
                button
                onClick={onTogglePictureInPicture}
            />,
            hidden: !isPictureInPictureEnabled || !audioVideoActivity || !pictureInPicture.supported
        },
        {
            content: <Icon name={fullScreenVideo ? 'shrink' : 'expand'} button onClick={onToggleFullScreenVideo} />,
            // Workaround: incorrect view with no pinned participant on mobile
            hidden: !audioVideoActivity || !videoPinParticipant || audioVideoViewConference
        },
        {
            content: <UserCameraSwitcher />,
            hidden: !canSwitchCamera || !audioVideoActivity || videoDevices?.length < 2
        },
        {
            content: (
                <div className={cx('tw-flex', {'tw-text-primary': sidebarView === SidebarView.Participants})}>
                    <Icon
                        name='users'
                        color='dark'
                        button
                        primary={sidebarView === SidebarView.Participants}
                        className='tw-mr-1'
                        onClick={() => onSidebarViewChange(SidebarView.Participants)}
                    />
                    {session.participants.length + 1}
                </div>
            ),
            mobile: true
        },
    ], [session, settings, sidebarView, fullScreenVideo, audioVideoActivity, localParticipant, userContext, audioVideoViewConference,
        canSwitchCamera, videoDevices, participantsNetworkQuality, timer, pictureInPicture, isPictureInPictureEnabled,
        videoPinParticipant, onSidebarViewChange, onToggleFullScreenVideo, onTogglePictureInPicture]);

    useEffect(() => {
        if (showNotification) {
            notifier.info({title: t(SESSION_T_KEYS.LIMITS.MAX_DURATION_EXCEEDED_SOON), icon: 'clock'});
        }
    }, [showNotification, t]);

    return (
        <div className={cx('tw-text-sm tw-w-full tw-bg-light tw-px-4', {'-md:tw-rounded-b-2xl': mobileActionsOpen})}>
            <div className='tw-h-10 tw-flex tw-items-center md:tw-justify-end tw-justify-between'>
                <Icon
                    className='md:tw-hidden tw-mr-2'
                    name='menu'
                    button
                    margin={false}
                    primary={mobileActionsOpen}
                    onClick={toggleMobileActionsOpen}
                />
                <div className='tw-flex tw-items-center'>
                    {map(filter(items, (item) => !item.hidden), (item, index) => (
                        <div key={index} className={cx('tw-flex tw-items-center', {'-md:tw-hidden': !item.mobile})}>
                            {index > 0 && <span className='tw-w-1 tw-h-1 tw-rounded tw-bg-default tw-mx-2' />}
                            {item.content}
                        </div>
                    ))}
                </div>
            </div>
            {mobileActionsOpen &&
                <div className='tw-py-2 tw-flex tw-justify-end tw-items-center md:tw-hidden tw-border-t tw-border-dark'>
                    {map(filter(items, (item) => !item.hidden && !item.mobile), (item, index) => (
                        <div key={index} className={cx('tw-flex tw-items-center')}>
                            {index > 0 && <span className='tw-w-1 tw-h-1 tw-rounded tw-bg-default tw-mx-2' />}
                            {item.content}
                        </div>
                    ))}
                </div>}
        </div>
    );
};
